import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { preparePayment, initializeHubtelPayment, sendPaymentOtp, verifyPaymentOtp, getOrder } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import logo from '../logo.png';
import config from '../config';
import { formatCurrency } from '../utils/currency';

function Payment({ user, cart, onCartUpdate, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const { app } = config;
  const APP_NAME = app.name;
  
  // Get order from location state
  const orderId = location.state?.orderId;
  const order = location.state?.order;
  const selectedItemsFromState = useMemo(() => location.state?.selectedItems || [], [location.state?.selectedItems]);
  // When retry has orderId but no items (e.g. refresh or different API shape), fetch order and use its items
  const [resolvedOrder, setResolvedOrder] = useState(null);
  const [resolvedSelectedItems, setResolvedSelectedItems] = useState([]);
  const [fetchingOrder, setFetchingOrder] = useState(false);
  const [fetchOrderTried, setFetchOrderTried] = useState(false);
  const selectedItems = (resolvedSelectedItems?.length > 0 ? resolvedSelectedItems : selectedItemsFromState) || [];
  
  // Debug logging
  useEffect(() => {
    console.log('Payment component mounted/updated:', {
      orderId,
      order,
      hasSelectedItems: selectedItems?.length > 0,
      locationState: location.state,
    });
  }, [orderId, order, selectedItems, location.state]);
  
  // Check if this is a retry after failed payment
  const isRetryPayment = location.state?.retryPayment || false;
  
  useEffect(() => {
    if (location.state?.error) {
      showError(location.state.error);
    }
    if (isRetryPayment) {
      showSuccess('Please complete the payment to proceed with your order.');
    }
  }, [location.state?.error, isRetryPayment, showError, showSuccess]);
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('mobile_money'); // Default payment method
  const [paymentDetails, setPaymentDetails] = useState({
    phone: '',
    network: 'mtn', // mtn, vodafone, airteltigo
  });
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpPhoneNumber, setOtpPhoneNumber] = useState(''); // Store phone number used for OTP

  // Card payment no longer collects details - redirects directly to Hubtel
  const getNormalizedPaymentMethod = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    // Fallback to mobile money unless card is explicitly selected.
    return normalized === 'card' ? 'card' : 'mobile_money';
  };

  // Detect network based on phone number prefix
  const detectNetworkFromPhone = (phoneNumber) => {
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Get first 3 digits for prefix detection
    if (cleaned.length >= 3) {
      const prefix = cleaned.substring(0, 3);
      
      // MTN prefixes: 024, 025, 053, 054, 055, 059
      if (['024', '025', '053', '054', '055', '059'].includes(prefix)) {
        return 'mtn';
      }
      
      // Vodafone prefixes: 020, 050
      if (['020', '050'].includes(prefix)) {
        return 'vodafone';
      }
      
      // AirtelTigo prefixes: 026, 056, 027, 057
      if (['026', '056', '027', '057'].includes(prefix)) {
        return 'airteltigo';
      }
    }
    
    // Return null if no network detected (keep current selection)
    return null;
  };

  // Handle phone number change: digits only, max 10; auto-detect network
  const handlePhoneNumberChange = (phoneNumber) => {
    const sanitized = phoneNumber.replace(/\D/g, '').slice(0, 10);
    const detectedNetwork = detectNetworkFromPhone(sanitized);
    setPaymentDetails(prev => ({
      ...prev,
      phone: sanitized,
      network: detectedNetwork || prev.network
    }));
  };

  // When we have orderId but no items (e.g. retry after refresh or API returns different shape), fetch order once
  useEffect(() => {
    if (!orderId || !user || fetchingOrder || fetchOrderTried) return;
    if (selectedItemsFromState?.length > 0) return; // Already have items from state
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) return;
    setFetchOrderTried(true);
    setFetchingOrder(true);
    getOrder(orderId, token)
      .then((fetchedOrder) => {
        const items = fetchedOrder?.orderItems ?? fetchedOrder?.order_items ?? [];
        if (Array.isArray(items) && items.length > 0) {
          setResolvedOrder(fetchedOrder);
          setResolvedSelectedItems(items);
        }
      })
      .catch((err) => {
        console.error('Payment: failed to fetch order for retry', err);
      })
      .finally(() => {
        setFetchingOrder(false);
      });
  }, [orderId, user, selectedItemsFromState?.length, fetchingOrder, fetchOrderTried]);

  useEffect(() => {
    // Redirect if no order ID
    if (!orderId) {
      console.error('Order ID missing in location state:', location.state);
      showError('Order information is missing. Please return to checkout.');
      navigate('/checkout');
      return;
    }
    // Redirect if not authenticated
    if (!user) {
      navigate('/');
      return;
    }
    // Redirect when we have no items and we're not loading (and we already tried fetching for retry)
    if (!fetchingOrder && (!selectedItems || selectedItems.length === 0) && fetchOrderTried) {
      console.error('Selected items missing in location state:', location.state);
      showError('Order details could not be loaded. Please try again from your orders.');
      navigate('/checkout');
      return;
    }
    
    console.log('Payment page loaded with orderId:', orderId, 'order:', order);
    
    // Initialize payment phone from user or order (use resolved order for retry if available)
    const orderForPhone = resolvedOrder || order;
    if (orderForPhone?.phone) {
      const detectedNetwork = detectNetworkFromPhone(orderForPhone.phone);
      setPaymentDetails(p => ({ 
        ...p, 
        phone: orderForPhone.phone,
        network: detectedNetwork || p.network
      }));
    } else if (user.phone) {
      const detectedNetwork = detectNetworkFromPhone(user.phone);
      setPaymentDetails(p => ({ 
        ...p, 
        phone: user.phone,
        network: detectedNetwork || p.network
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orderId, order, resolvedOrder, selectedItems, selectedItemsFromState?.length, fetchingOrder, fetchOrderTried, navigate]);

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => {
      const price = parseFloat(item.price ?? item.unit_price) || 0;
      const quantity = item.quantity || 1;
      return sum + (price * quantity);
    }, 0);
  };

  // Card payment handlers removed - redirects directly to Hubtel

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (showOtpModal) {
      showError('Please verify the OTP code before proceeding.');
      return;
    }
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    // Validate orderId is present
    if (!orderId) {
      console.error('Order ID missing when submitting payment:', { orderId, locationState: location.state });
      showError('Order ID is missing. Please return to checkout and try again.');
      navigate('/checkout');
      return;
    }
    
    const selectedPaymentMethod = getNormalizedPaymentMethod(paymentMethod);

    // Validation based on payment method
    if (selectedPaymentMethod === 'mobile_money') {
      // Clean phone number (remove spaces, dashes, etc.)
      const cleanedPhone = paymentDetails.phone.replace(/\D/g, '');
      
      if (!paymentDetails.phone || cleanedPhone.length < 10) {
        showError('Please enter a valid mobile money phone number (e.g., 0244123456).');
        return;
      }
      
      // Validate phone number format (should be 10 digits starting with 0, or 12 digits with country code)
      if (cleanedPhone.length === 10 && !cleanedPhone.startsWith('0')) {
        showError('Please enter a valid Ghana mobile number starting with 0 (e.g., 0244123456).');
        return;
      }
      
      if (cleanedPhone.length > 12) {
        showError('Phone number is too long. Please enter a valid mobile number (e.g., 0244123456).');
        return;
      }
      
      // Validate network is selected
      if (!paymentDetails.network) {
        showError('Please select your mobile money network.');
        return;
      }
    } else if (selectedPaymentMethod === 'card') {
      // No validation needed - card details will be collected on Hubtel's page
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        navigate('/');
        return;
      }
      
      console.log('=== PAYMENT FLOW START ===');
      console.log('=== RAW VALUES ===');
      console.log('orderId:', orderId, 'type:', typeof orderId);
      console.log('paymentMethod:', paymentMethod, 'type:', typeof paymentMethod);
      console.log('selectedPaymentMethod:', selectedPaymentMethod);
      console.log('paymentMethod value (JSON):', JSON.stringify(paymentMethod));
      console.log('paymentDetails:', paymentDetails);
      console.log('=== END RAW VALUES ===');
      
      // Validate orderId is present and valid
      if (!orderId || orderId === null || orderId === undefined) {
        throw new Error('Order ID is required but was not provided. Please return to checkout.');
      }
      
      // Ensure orderId is an integer
      const orderIdInt = parseInt(orderId, 10);
      if (isNaN(orderIdInt) || orderIdInt <= 0) {
        throw new Error('Invalid order ID. Please return to checkout.');
      }
      
      // Handle payment based on method
      console.log('=== CHECKING PAYMENT METHOD ===');
      console.log('Payment method value:', paymentMethod);
      console.log('Payment method type:', typeof paymentMethod);
      console.log('Payment method === "mobile_money"?', paymentMethod === 'mobile_money');
      console.log('Payment method == "mobile_money"?', paymentMethod == 'mobile_money');
      console.log('String comparison:', String(paymentMethod) === 'mobile_money');
      console.log('Trimmed comparison:', String(paymentMethod).trim() === 'mobile_money');
      console.log('Payment details:', paymentDetails);
      
      // Use strict comparison and also check string version - be very explicit
      const isMobileMoney = selectedPaymentMethod === 'mobile_money';
      console.log('isMobileMoney (final):', isMobileMoney);
      
      if (isMobileMoney) {
        console.log('=== MOBILE MONEY PATH - SENDING OTP ===');
        console.log('Mobile money payment detected - sending OTP first');
        // For mobile money: Send OTP first, then verify before initializing payment
        const cleanedPhone = paymentDetails.phone.replace(/\D/g, '');
        
        console.log('Phone details:', { original: paymentDetails.phone, cleaned: cleanedPhone, length: cleanedPhone.length });
        
        // Validate phone number is provided
        if (!cleanedPhone || cleanedPhone.length < 9) {
          console.error('Phone validation failed:', { cleanedPhone, length: cleanedPhone?.length });
          showError('Please enter a valid phone number');
          setLoading(false);
          return;
        }
        
        try {
          // Send OTP to phone number
          console.log('=== ABOUT TO CALL sendPaymentOtp API ===');
          console.log('Parameters:', { orderIdInt, cleanedPhone, tokenLength: token?.length });
          console.log('Sending OTP to phone:', cleanedPhone, 'for order:', orderIdInt);
          const otpResponse = await sendPaymentOtp(orderIdInt, cleanedPhone, token);
          console.log('=== OTP API CALL COMPLETED ===');
          console.log('OTP sent response:', otpResponse);
          
          // Check if response has success property
          if (otpResponse && (otpResponse.success === true || otpResponse.success === 'true')) {
            setOtpPhoneNumber(cleanedPhone); // Store phone number for modal display
            setOtpSent(true);
            setShowOtpModal(true); // Open modal immediately
            setOtp(''); // Clear any previous OTP
            // No toast notification - modal opening is sufficient feedback
          } else {
            // Response might not have success property, but if we got here without error, assume success
            if (otpResponse && !otpResponse.error) {
              setOtpPhoneNumber(cleanedPhone); // Store phone number for modal display
              setOtpSent(true);
              setShowOtpModal(true); // Open modal immediately
              setOtp(''); // Clear any previous OTP
              // No toast notification - modal opening is sufficient feedback
            } else {
              const errorMsg = otpResponse?.message || otpResponse?.error || 'Failed to send OTP. Please try again.';
              console.error('OTP send failed:', errorMsg, otpResponse);
              showError(errorMsg);
            }
          }
        } catch (error) {
          console.error('Error sending OTP:', error);
          // Extract error message from various possible formats
          let errorMsg = 'Failed to send OTP. Please try again.';
          if (error?.response?.data?.message) {
            errorMsg = error.response.data.message;
          } else if (error?.message) {
            errorMsg = error.message;
          } else if (typeof error === 'string') {
            errorMsg = error;
          }
          console.error('OTP Error details:', { error, errorMsg });
          showError(errorMsg);
        }
        setLoading(false);
        return; // Stop here, wait for OTP verification
        
      } else if (selectedPaymentMethod === 'card') {
        // Card payment: redirect directly to Hubtel's payment page (no card details collected)
        const paymentPayload = { order_id: orderIdInt, payment_method: 'card' };
        const prepareResult = await preparePayment(paymentPayload, token);
        const paymentData = await initializeHubtelPayment(
          prepareResult.payment_reference,
          token,
          'card',
          null,
          null,
          null // No card details - user will enter on Hubtel's page
        );
        const url = paymentData.authorization_url || paymentData.payment_url || paymentData.checkout_url;
        if (url) {
          sessionStorage.setItem('pending_payment_reference', prepareResult.payment_reference);
          sessionStorage.setItem('pending_order_id', orderIdInt.toString());
          sessionStorage.setItem('pending_invoice_number', prepareResult.invoice_number || order?.invoice_number);
          window.location.href = url;
          return;
        }
        throw new Error(paymentData.message || 'Failed to initialize card payment.');
      } else if (selectedPaymentMethod === 'cash_on_delivery') {
        // For cash on delivery, update order payment status
        // Order already exists, just update payment method
        const { updateOrder } = await import('../services/api');
        await updateOrder(orderId, {
          payment_method: selectedPaymentMethod,
          payment_status: 'paid', // Cash on delivery is considered paid
        }, token);
        
        // Clear the entire shopping cart after successful order
        if (onCartUpdate) {
          onCartUpdate([]);
        }
        localStorage.setItem('cart', JSON.stringify([]));
        
        navigate('/orders', { 
          state: { 
            orderSuccess: true, 
            orderNumber: order?.order_number 
          } 
        });
        
        showSuccess(`Order ${order?.order_number} placed successfully! You will pay on delivery.`);
      }
    } catch (error) {
      console.error('Failed to process payment and create order:', error);
      
      // Provide more helpful error messages
      let errorMessage = error.message || 'Failed to process payment. Please try again.';
      
      // Check for specific error patterns
      if (errorMessage.includes('phone number') || errorMessage.includes('phone')) {
        errorMessage = 'Invalid phone number. Please check that your mobile number is correct and matches the selected network (MTN, Vodafone, or AirtelTigo).';
      } else if (errorMessage.includes('network') || errorMessage.includes('channel')) {
        errorMessage = 'Invalid network selection. Please select the correct mobile money network for your phone number.';
      } else if (errorMessage.includes('USSD')) {
        errorMessage = 'Failed to send payment prompt. Please verify your phone number is correct and try again.';
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification and proceed with payment
  const handleOtpVerification = async () => {
    if (!otp || otp.length !== 6) {
      showError('Please enter a valid 6-digit OTP');
      return;
    }
    const selectedPaymentMethod = getNormalizedPaymentMethod(paymentMethod);

    setOtpLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        navigate('/');
        return;
      }

      const orderIdInt = parseInt(orderId, 10);
      
      // Use the same phone number that was used when sending OTP
      // This ensures consistency between OTP verification and payment initialization
      const phoneToUse = otpPhoneNumber || paymentDetails.phone || user?.phone || user?.mobile_number || order?.phone;
      const cleanedPhone = phoneToUse ? phoneToUse.replace(/\D/g, '') : '';

      console.log('Verifying OTP with phone:', cleanedPhone ? cleanedPhone.substring(0, 5) + '****' : 'none');
      console.log('OTP to verify:', otp);
      console.log('Order ID:', orderIdInt);

      // Verify OTP (use the same phone number that was used to send OTP)
      const verifyResponse = await verifyPaymentOtp(orderIdInt, cleanedPhone, otp, token);
      
      console.log('OTP verification response:', verifyResponse);
      
      // Ensure verification was successful
      if (!verifyResponse || !verifyResponse.success) {
        throw new Error(verifyResponse?.message || 'OTP verification failed. Please try again.');
      }

      console.log('OTP verified successfully, proceeding with payment initialization');

      // OTP verified - proceed with payment initialization
      setShowOtpModal(false);
      setOtp('');
      setOtpSent(false);
      
      // Store the phone number temporarily before clearing otpPhoneNumber
      // IMPORTANT: Keep the exact same phone number format used for OTP verification
      // Use otpPhoneNumber if available (from OTP send), otherwise use cleanedPhone
      const verifiedPhone = otpPhoneNumber ? otpPhoneNumber.replace(/\D/g, '') : cleanedPhone;
      
      console.log('OTP verified, stored verified phone:', verifiedPhone ? verifiedPhone.substring(0, 5) + '****' : 'none');
      console.log('Full verified phone (for debugging):', verifiedPhone);
      console.log('OTP phone number used:', otpPhoneNumber ? otpPhoneNumber.substring(0, 5) + '****' : 'none');
      console.log('Cleaned phone from form:', cleanedPhone ? cleanedPhone.substring(0, 5) + '****' : 'none');
      
      // Validate that we have a phone number
      if (!verifiedPhone || verifiedPhone.length < 9) {
        console.error('No valid phone number available for payment initialization');
        showError('Phone number is required for payment. Please try again.');
        setOtpLoading(false);
        return;
      }
      
      // Don't clear otpPhoneNumber yet - keep it for payment initialization
      // setOtpPhoneNumber(''); // Keep this for now
      
      // Wait longer to ensure cache is fully updated on backend and verification flag is set
      // Increased delay to ensure cache consistency across distributed systems
      console.log('Waiting for backend cache to update...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Increased from 500ms to 1000ms
      console.log('Proceeding with payment initialization...');
      
      // Now initialize payment
      setLoading(true);
      
      // Step 1: Prepare payment
      const paymentPayload = {
        order_id: orderIdInt,
        payment_method: selectedPaymentMethod,
      };
      
      const prepareResult = await preparePayment(paymentPayload, token);
      
      console.log('Preparing to initialize payment with:', {
        payment_reference: prepareResult.payment_reference,
        payment_method: paymentMethod,
        selected_payment_method: selectedPaymentMethod,
        verified_phone: verifiedPhone ? verifiedPhone.substring(0, 5) + '****' : 'none',
        verified_phone_full: verifiedPhone, // Log full phone for debugging (remove in production)
        verified_phone_length: verifiedPhone ? verifiedPhone.length : 0,
        network: paymentDetails.network || null,
        is_retry: isRetryPayment,
        otp_phone_number: otpPhoneNumber ? otpPhoneNumber.substring(0, 5) + '****' : 'none',
        payment_details_phone: paymentDetails.phone ? paymentDetails.phone.substring(0, 5) + '****' : 'none',
      });
      
      // Validate phone number before sending
      if (selectedPaymentMethod === 'mobile_money' && !verifiedPhone) {
        console.error('No verified phone number available for payment initialization');
        showError('Phone number is required for payment. Please try again.');
        setOtpLoading(false);
        return;
      }
      
      if (selectedPaymentMethod === 'mobile_money' && !paymentDetails.network) {
        console.error('No network selected for payment initialization');
        showError('Mobile money network is required. Please select your network.');
        setOtpLoading(false);
        return;
      }
      
      // Step 2: Initialize Hubtel payment
      // CRITICAL: Use the exact same phone number that was used for OTP verification
      // This must match exactly what was stored in the verification flag
      console.log('Calling initializeHubtelPayment with:', {
        payment_reference: prepareResult.payment_reference,
        payment_method: selectedPaymentMethod,
        phone: verifiedPhone ? verifiedPhone.substring(0, 5) + '****' : 'none',
        phone_length: verifiedPhone ? verifiedPhone.length : 0,
        network: paymentDetails.network,
      });
      
      const paymentData = await initializeHubtelPayment(
        prepareResult.payment_reference,
        token,
        selectedPaymentMethod,
        verifiedPhone || null, // Use the verified phone number (must match OTP verification)
        paymentDetails.network || null // null for card
      );
      
      console.log('Payment initialization response:', {
        has_ussd_prompt: !!paymentData.ussd_prompt,
        success: paymentData.success,
        message: paymentData.message,
      });
      
      // Clear otpPhoneNumber after successful payment initialization
      setOtpPhoneNumber('');

      // Handle USSD prompt (mobile money); card redirect happens in handleSubmit, no OTP
      if (paymentData.ussd_prompt) {
        // Clear cart
        if (onCartUpdate) {
          onCartUpdate([]);
        }
        localStorage.setItem('cart', JSON.stringify([]));
        
        // Store order info for polling on orders page
        sessionStorage.setItem('pending_payment_reference', prepareResult.payment_reference);
        sessionStorage.setItem('pending_order_id', orderIdInt.toString());
        sessionStorage.setItem('pending_invoice_number', prepareResult.invoice_number || order?.invoice_number);
        sessionStorage.setItem('pending_payment_method', selectedPaymentMethod);
        
        // No toast notification - redirecting to orders page is sufficient feedback
        
        // Redirect to orders page immediately - polling will happen there
        setTimeout(() => {
          navigate('/orders', {
            state: {
              orderSuccess: true,
              orderNumber: order?.order_number,
              invoiceNumber: prepareResult.invoice_number || (resolvedOrder || order)?.invoice_number,
              pendingPayment: true,
              orderId: orderIdInt,
            }
          });
        }, 1500);
        
        setLoading(false);
      } else {
        throw new Error(paymentData.message || 'Failed to initialize payment');
      }
    } catch (error) {
      console.error('Failed to verify OTP or initialize payment:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack,
      });
      
      let errorMessage = error.message || 'Failed to verify OTP. Please try again.';
      
      // Check if this is an OTP verification error or payment initialization error
      if (error?.response?.data?.otp_required) {
        errorMessage = 'OTP verification required. Please verify your OTP first. If you just verified your OTP, please wait a moment and try again.';
      } else if (error?.response?.data?.error_type === 'ussd_prompt_failed') {
        // Mobile money prompt failed to send
        errorMessage = error.response.data.message || 'Failed to send mobile money prompt. Please try again.';
        // Don't clear OTP state - allow user to retry
        setOtpLoading(false);
        return; // Return early to keep OTP modal open for retry
      } else if (errorMessage.includes('OTP')) {
        errorMessage = 'Invalid or expired OTP. Please try again.';
      } else if (errorMessage.includes('Phone number is required')) {
        errorMessage = 'Phone number is required for mobile money payment. Please ensure your phone number is entered correctly.';
      } else if (errorMessage.includes('Network/channel is required')) {
        errorMessage = 'Mobile money network is required. Please select your network (MTN, Vodafone, or AirtelTigo).';
      } else if (errorMessage.includes('OTP verification required')) {
        errorMessage = 'OTP verification required. Please verify your OTP first. If you just verified, please wait a moment and try again.';
      } else if (errorMessage.includes('Payment gateway returned an error')) {
        // Generic payment gateway error - try to get more details from response
        if (error?.response?.data?.response) {
          const hubtelResponse = error.response.data.response;
          if (hubtelResponse?.message || hubtelResponse?.Message) {
            errorMessage = hubtelResponse.message || hubtelResponse.Message || errorMessage;
          }
          if (hubtelResponse?.responseCode || hubtelResponse?.ResponseCode) {
            const code = hubtelResponse.responseCode || hubtelResponse.ResponseCode;
            errorMessage += ` (Hubtel Code: ${code})`;
          }
        }
        // If no specific details, add generic guidance
        if (errorMessage === 'Payment gateway returned an error' || errorMessage.includes('Payment gateway returned an error') && !errorMessage.includes('Code:')) {
          errorMessage = errorMessage + ' Please check your phone number and network selection, then try again.';
        }
      }
      
      showError(errorMessage);
      setOtpLoading(false);
    }
  };

  // Card OTP handler removed - card payments redirect to Hubtel

  // Resend OTP
  const handleResendOtp = async () => {
    setOtpLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }

      const orderIdInt = parseInt(orderId, 10);
      
      // Get phone from payment details, user profile, or order
      const phoneToUse = paymentDetails.phone || user?.phone || user?.mobile_number || order?.phone;
      const cleanedPhone = phoneToUse ? phoneToUse.replace(/\D/g, '') : '';

      await sendPaymentOtp(orderIdInt, cleanedPhone || '', token);
      setOtpPhoneNumber(cleanedPhone || ''); // Update phone number for modal display
      setOtp('');
      // No toast notification - OTP modal is already open
    } catch (error) {
      showError(error.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const total = calculateTotal();
  const cartButtonRef = React.useRef(null);

  return (
    <div className="App">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={cart?.length || 0}
        onCartClick={() => navigate('/')}
        cartButtonRef={cartButtonRef}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={() => {}}
        onLogout={onLogout}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-[100px] sm:pt-[120px] pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-1 h-8 sm:w-1.5 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight mb-1 sm:mb-2">Payment</h1>
                <p className="text-sm sm:text-base text-gray-600 font-medium">Complete your payment to finalize your order</p>
              </div>
            </div>
          </div>

          {/* Loading order details (retry with empty state) */}
          {fetchingOrder && selectedItems.length === 0 && (
            <div className="mb-6 flex items-center justify-center gap-3 py-8 rounded-xl bg-gray-100 border border-gray-200">
              <svg className="animate-spin h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-gray-700 font-medium">Loading order details...</span>
            </div>
          )}

          {/* Retry Payment Banner */}
          {isRetryPayment && (
            <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 sm:p-5 shadow-lg">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-bold text-yellow-900 text-base sm:text-lg mb-1">Retry Payment</h3>
                  <p className="text-sm sm:text-base text-yellow-800">
                    Your previous payment attempt failed. Please complete the payment below to proceed with your order.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Payment Methods */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                {/* Payment Method Selection */}
                <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Select Payment Method</h2>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Mobile Money Option */}
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'mobile_money' 
                        ? 'border-primary bg-primary/5 shadow-lg' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mobile_money"
                        checked={paymentMethod === 'mobile_money'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mt-1 w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-base sm:text-lg">Mobile Money</h3>
                            <p className="text-xs sm:text-sm text-gray-600">Pay with MTN, Vodafone, or AirtelTigo</p>
                          </div>
                        </div>
                        {paymentMethod === 'mobile_money' && (
                          <div className="mt-4 space-y-4 pl-14">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                              <p className="text-xs text-green-800">
                                After confirming your OTP, you will receive a prompt to confirm payment. If you don&apos;t get the prompt, dial *170# and check your approvals.
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2">
                                Mobile Money Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="tel"
                                value={paymentDetails.phone}
                                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                maxLength={10}
                                inputMode="numeric"
                                autoComplete="tel"
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white"
                                placeholder="0244123456"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2">
                                Network <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={paymentDetails.network}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, network: e.target.value })}
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white"
                                required
                              >
                                <option value="mtn">MTN Mobile Money</option>
                                <option value="vodafone">Vodafone Cash</option>
                                <option value="airteltigo">AirtelTigo Money</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Card Payment Option — collect card number, expiry, CVV; then fire payment API; bank may send OTP */}
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'card'
                        ? 'border-primary bg-primary/5 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mt-1 w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-base sm:text-lg">Credit/Debit Card</h3>
                            <p className="text-xs sm:text-sm text-gray-600">Visa, Mastercard, or American Express</p>
                          </div>
                        </div>
                        {paymentMethod === 'card' && (
                          <div className="mt-4 pl-14">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-xs text-blue-800">
                                You will be redirected to Hubtel&apos;s secure payment page to enter your card details.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Cash on Delivery Option */}
                    {/* <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'cash_on_delivery' 
                        ? 'border-primary bg-primary/5 shadow-lg' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash_on_delivery"
                        checked={paymentMethod === 'cash_on_delivery'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mt-1 w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-base sm:text-lg">Cash on Delivery</h3>
                            <p className="text-xs sm:text-sm text-gray-600">Pay when you receive your order</p>
                          </div>
                        </div>
                      </div>
                    </label> */}
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1 order-1 lg:order-2">
                <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200/50 p-4 sm:p-6 shadow-lg lg:sticky lg:top-24">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Order Summary</h2>
                  
                  {/* Order Items */}
                  <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-64 sm:max-h-96 overflow-y-auto">
                    {selectedItems.map((item) => {
                      const itemPrice = parseFloat(item.price ?? item.unit_price) || 0;
                      const quantity = item.quantity || 1;
                      const itemTotal = itemPrice * quantity;
                      // Support both checkout cart shape (name, image, selectedColor/Size) and API order item shape (product.*, color, size)
                      const itemName = item.name ?? item.product?.name ?? `Product #${item.product_id ?? item.id}`;
                      const itemImage = item.image ?? item.product?.image ?? item.product?.images?.[0] ?? 'https://via.placeholder.com/100';
                      const itemColor = item.selectedColor ?? item.color;
                      const itemSize = item.selectedSize ?? item.size;
                      return (
                        <div key={item.id ?? `${item.product_id}-${item.quantity}-${itemColor}-${itemSize}`} className="flex gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200 last:border-0">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img 
                              src={itemImage} 
                              alt={itemName}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = 'https://via.placeholder.com/100'; }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{itemName}</h3>
                            {(itemColor || itemSize) && (
                              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                                {itemColor && (
                                  <span className="text-[10px] sm:text-xs text-gray-600">Color: {itemColor}</span>
                                )}
                                {itemSize && (
                                  <span className="text-[10px] sm:text-xs text-gray-600">Size: {itemSize}</span>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-1.5 sm:mt-2">
                              <span className="text-[10px] sm:text-xs text-gray-600">Qty: {quantity}</span>
                              <span className="font-bold text-primary text-sm sm:text-base">{formatCurrency(itemTotal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Total */}
                  <div className="border-t-2 border-gray-200 pt-3 sm:pt-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-base sm:text-lg font-semibold text-gray-700 whitespace-nowrap">Total</span>
                      <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent text-right flex-shrink-0">
                        {formatCurrency(total)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 text-center italic">
                      Additional service charges may apply.
                    </p>
                    
                    <button
                      type="submit"
                      disabled={loading || selectedItems.length === 0 || showOtpModal}
                      className="w-full py-3 sm:py-4 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-300 transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm sm:text-base">Processing payment...</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Pay {formatCurrency(total)} now
                        </span>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => navigate('/checkout')}
                      className="w-full py-2.5 sm:py-3 text-sm sm:text-base text-gray-600 hover:text-gray-900 font-semibold rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all"
                    >
                      Back to Checkout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-xs sm:max-w-sm w-full p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Verify Payment</h2>
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp('');
                  setOtpSent(false);
                  setOtpPhoneNumber('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                Code sent to <strong>{(otpPhoneNumber || paymentDetails.phone || user?.phone || user?.mobile_number || order?.phone || '').replace(/(\d{3})(\d{3})(\d{4})/, '$1***$3')}</strong>
              </p>
              
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Enter Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                }}
                onKeyDown={(e) => {
                  // Prevent form submission when Enter is pressed in OTP field
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (otp.length === 6 && !otpLoading) {
                      handleOtpVerification();
                    }
                  }
                }}
                placeholder="000000"
                maxLength={6}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 text-center text-xl sm:text-2xl font-bold tracking-widest"
                autoFocus
              />
              
              {otpSent && (
                <p className="mt-2 text-xs text-gray-500">
                  Didn't receive it?{' '}
                  <button
                    onClick={handleResendOtp}
                    disabled={otpLoading}
                    className="text-primary font-semibold hover:underline disabled:opacity-50"
                  >
                    Resend
                  </button>
                </p>
              )}
            </div>

            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp('');
                  setOtpSent(false);
                  setOtpPhoneNumber('');
                }}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={otpLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleOtpVerification}
                disabled={otpLoading || otp.length !== 6}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-primary text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {otpLoading ? 'Verifying...' : 'Verify & Pay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card OTP modal removed - card payments redirect to Hubtel */}
    </div>
  );
}

export default Payment;
